import { useState } from "react";
import {
  createTopicInlineAction,
  type TopicInlineChoice,
} from "@/actions/topics";
import { SelectField, type SelectOption } from "@/components/Field";
import { InlineCreate } from "@/components/InlineCreate";
import styles from "./TopicField.module.css";

export type TopicChoice = {
  id: number;
  code: string;
  label: string | null;
};

type TopicFieldProps = {
  topics: TopicChoice[];
  selectedId: string;
  errors?: string[];
};

const NONE_OPTION: SelectOption = { value: "", label: "— none —" };

const toOption = (topic: TopicChoice): SelectOption => ({
  value: String(topic.id),
  label: topic.label ?? topic.code,
});

// The select plus the one thing that could not be done from it. Topics are a
// short closed list, so it stays a select rather than becoming a search —
// what was missing was a way to add the shelf a new quest belongs on without
// abandoning the quest.
export const TopicField = ({ topics, selectedId, errors }: TopicFieldProps) => {
  const [choices, setChoices] = useState(topics);
  const [currentId, setCurrentId] = useState(selectedId);

  // Selected as well as listed: a topic minted while writing a template is the
  // topic that template belongs on, and re-picking it from the select is a
  // step with no decision in it.
  const addChoice = (choice: TopicInlineChoice) => {
    setChoices((current) => [...current, choice]);
    setCurrentId(String(choice.id));
  };

  return (
    <div className={styles.field}>
      <SelectField
        name="topicId"
        label="Topic"
        hint="Required before publishing."
        options={[NONE_OPTION, ...choices.map(toOption)]}
        defaultValue={currentId}
        errors={errors}
      />
      <InlineCreate
        triggerLabel="New topic"
        title="Create a topic"
        note="A topic is a browse shelf, not a skill: it is where a quest is found, never what completing it builds. New shelves land first in the browse order — fix that on the Topics page."
        labelPlaceholder="Mindfulness"
        codePlaceholder="MINDFULNESS"
        existing={choices}
        action={createTopicInlineAction}
        onCreated={addChoice}
      />
    </div>
  );
};
